import React, { useState, useEffect } from 'react';
import {
  QUESTION_LIBRARY,
  QUESTION_LIBRARY_CATEGORIES,
} from '../data/questionLibrary';

const AllQuestionsModal = ({
  isOpen,
  onClose,
  onQuestionClick,
  initialCategory = 'all',
}) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredQuestion, setHoveredQuestion] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelectedCategory(initialCategory || 'all');
    setSearchQuery('');
    setHoveredQuestion(null);
  }, [initialCategory, isOpen]);

  // Filter questions based on category and search query
  const filteredQuestions = QUESTION_LIBRARY.filter(question => {
    const matchesCategory =
      selectedCategory === 'all' ||
      question.sidebarCategory === selectedCategory;
    const matchesSearch = question.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      question.sidebarCategory.toLowerCase().includes(searchQuery.toLowerCase()) ||
      question.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      question.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const handleQuestionClick = (question) => {
    onQuestionClick(question.question);
    onClose();
  };

  const getPopularityColor = (popularity) => {
    if (popularity >= 90) return '#10B981'; // Emerald
    if (popularity >= 80) return '#3B82F6'; // Blue
    return '#6B7280'; // Gray
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Daily Life & Dharma': 'bg-blue-500',
      'Relationships & Family': 'bg-pink-500',
      'Career & Duty': 'bg-indigo-500',
      'Festivals & Rituals': 'bg-amber-500',
      'Philosophy & Spiritual Growth': 'bg-emerald-500',
    };
    return colors[category] || 'bg-gray-500';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden border border-cyan-500/20">
          
          {/* Header */}
          <div className="relative bg-gradient-to-r from-cyan-900/50 to-blue-900/50 p-6 border-b border-cyan-500/30">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Spiritual Wisdom Library
                </h2>
                <p className="text-gray-300 mt-2">
                  Explore {QUESTION_LIBRARY.length} carefully curated questions for modern spiritual seekers
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-cyan-400 transition-colors duration-200 p-2 rounded-lg hover:bg-cyan-500/10"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Search and Filter Bar */}
            <div className="flex flex-col sm:flex-row gap-4 mt-6">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search questions or topics..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-gray-800/50 border border-cyan-500/30 rounded-xl py-3 px-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200"
                />
                <svg className="absolute right-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              
              <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
                {QUESTION_LIBRARY_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-4 py-2 rounded-xl whitespace-nowrap transition-all duration-200 ${
                      selectedCategory === cat.id
                        ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/25'
                        : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 border border-cyan-500/10'
                    }`}
                  >
                    {cat.name} ({cat.count})
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Questions Grid */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            <div className="grid gap-4">
              {filteredQuestions.map((question) => (
                <div
                  key={question.id}
                  onMouseEnter={() => setHoveredQuestion(question.id)}
                  onMouseLeave={() => setHoveredQuestion(null)}
                  onClick={() => handleQuestionClick(question)}
                  className={`bg-gray-800/30 border rounded-2xl p-6 cursor-pointer transition-all duration-300 group ${
                    hoveredQuestion === question.id
                      ? 'border-cyan-500/50 bg-gradient-to-r from-cyan-900/20 to-blue-900/20 shadow-xl shadow-cyan-500/10 transform scale-105'
                      : 'border-cyan-500/10 hover:border-cyan-500/30'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white group-hover:text-cyan-300 transition-colors duration-200 mb-3">
                        {question.question}
                      </h3>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(question.sidebarCategory)} text-white`}>
                          {question.sidebarCategory}
                        </span>
                        {question.category !== question.sidebarCategory && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-300 border border-gray-600">
                            {question.category}
                          </span>
                        )}
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-300 border border-gray-600">
                          {question.scripture}
                        </span>
                        {question.tags.slice(0, 2).map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 rounded-full text-xs font-medium bg-cyan-900/30 text-cyan-300 border border-cyan-500/20"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 ml-4">
                      <div className="text-right">
                        <div className="text-sm text-gray-400">Popularity</div>
                        <div 
                          className="text-lg font-bold"
                          style={{ color: getPopularityColor(question.popularity) }}
                        >
                          {question.popularity}%
                        </div>
                      </div>
                      <div className="text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200 transform group-hover:translate-x-1">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredQuestions.length === 0 && (
              <div className="text-center py-12">
                <div className="text-cyan-400 text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-semibold text-gray-300 mb-2">No questions found</h3>
                <p className="text-gray-400">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-900/50 border-t border-cyan-500/10 p-4">
            <div className="flex items-center justify-between text-sm text-gray-400">
              <div className="flex items-center gap-4">
                <span>✨ {filteredQuestions.length} questions found</span>
                <span>•</span>
                <span>🕉️ Ancient wisdom for modern life</span>
              </div>
              <button
                onClick={onClose}
                className="px-4 py-2 text-cyan-400 hover:text-cyan-300 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllQuestionsModal;
